import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { baseURL } from '../../../api/api';
import { journalsApi, releasesApi } from '../../api/endpoints';
import DataTable from '../../components/DataTable';
import useAdminList from '../../hooks/useAdminList';
import { useUi } from '../../context/UiContext';
import styles from '../listPage.module.css';

const ReleasesList = () => {
    const { toast, confirm } = useUi();
    const location = useLocation();
    const list = useAdminList(releasesApi.list, { sort: 'date', dir: 'desc' });
    const { reload, setFilter } = list;
    const [journals, setJournals] = useState([]);

    useEffect(() => {
        let cancelled = false;
        journalsApi
            .list({ perPage: 200, sort: 'name', dir: 'asc' })
            .then((page) => !cancelled && setJournals(page.items))
            .catch((e) => !cancelled && toast.error(e.message));
        return () => {
            cancelled = true;
        };
    }, [toast]);

    // Переход со страницы журналов приходит с ?journal=<id> — сразу применяем фильтр.
    useEffect(() => {
        const journalId = new URLSearchParams(location.search).get('journal');
        if (journalId) setFilter('journal_id', journalId);
    }, [location.search, setFilter]);

    const handleDelete = async (row) => {
        const ok = await confirm({
            title: 'Удалить выпуск?',
            message:
                row.articles > 0
                    ? `Выпуск №${row.number || row.id}\n\nВ выпуске ${row.articles} статья(-и). Они останутся в базе, но потеряют привязку к выпуску.`
                    : `Выпуск №${row.number || row.id}\n\nСтатей в выпуске нет. Обложка и сборник будут удалены.`,
            confirmLabel: 'Удалить',
            danger: true,
        });
        if (!ok) return;

        try {
            await releasesApi.remove(row.id, { force: row.articles > 0 });
            toast.success('Выпуск удалён');
            reload();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const columns = [
        {
            key: 'number',
            title: 'Выпуск',
            sortable: true,
            render: (row) => (
                <div>
                    <NavLink to={`/admin/releases/${row.id}`} className={styles.primaryLink}>
                        №{row.number || '—'}
                        {row.tome ? `, том ${row.tome}` : ''}
                    </NavLink>
                    <div className={styles.subline}>{row.journal ? row.journal.name : 'без журнала'}</div>
                </div>
            ),
        },
        { key: 'issn', title: 'ISSN', width: 150, render: (row) => row.issn || <span className="adm-muted">—</span> },
        {
            key: 'date',
            title: 'Дата',
            width: 120,
            sortable: true,
            render: (row) => row.date || <span className="adm-muted">—</span>,
        },
        {
            key: 'articles',
            title: 'Статей',
            width: 100,
            render: (row) => {
                const query = `journal_id=${row.journal ? row.journal.id : ''}&release_id=${row.id}`;
                return row.articles > 0 ? (
                    <NavLink
                        to={`/admin/articles?${query}`}
                        className={styles.usageBadge}
                        title="Показать статьи этого выпуска"
                    >
                        {row.articles}
                    </NavLink>
                ) : row.journal ? (
                    <NavLink
                        to={`/admin/articles/new?journal=${row.journal.id}&release=${row.id}`}
                        className="adm-muted"
                        title="Добавить статью в этот выпуск"
                    >
                        + добавить
                    </NavLink>
                ) : (
                    <span className="adm-muted">—</span>
                );
            },
        },
        {
            key: 'files',
            title: 'Файлы',
            width: 150,
            render: (row) => (
                <div>
                    {row.file_path && (
                        <a href={baseURL + row.file_path} target="_blank" rel="noopener noreferrer">
                            обложка ↗
                        </a>
                    )}
                    {row.file_path && row.document && ' · '}
                    {row.document && (
                        <a href={baseURL + row.document} target="_blank" rel="noopener noreferrer">
                            сборник ↗
                        </a>
                    )}
                    {!row.file_path && !row.document && <span className="adm-muted">—</span>}
                </div>
            ),
        },
        {
            key: 'actions',
            title: '',
            width: 150,
            align: 'right',
            render: (row) => (
                <div className={styles.rowActions}>
                    <NavLink to={`/admin/releases/${row.id}`} className="adm-btn adm-btn-ghost adm-btn-sm">
                        Изменить
                    </NavLink>
                    <button
                        type="button"
                        className="adm-btn adm-btn-danger adm-btn-sm"
                        onClick={() => handleDelete(row)}
                    >
                        Удалить
                    </button>
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="adm-page-header">
                <div>
                    <h1 className="adm-page-title">Выпуски журналов</h1>
                    <p className="adm-page-subtitle">Номера, в которые входят статьи</p>
                </div>
                <NavLink to="/admin/releases/new" className="adm-btn adm-btn-primary">
                    + Добавить выпуск
                </NavLink>
            </div>

            <DataTable
                columns={columns}
                rows={list.rows}
                loading={list.loading}
                error={list.error}
                onRetry={list.reload}
                search={list.search}
                onSearchChange={list.setSearch}
                searchPlaceholder="Поиск по номеру, тому, ISSN"
                sort={list.sort}
                dir={list.dir}
                onSortChange={list.toggleSort}
                page={list.page}
                perPage={list.perPage}
                total={list.total}
                lastPage={list.lastPage}
                onPageChange={list.setPage}
                onPerPageChange={list.setPerPage}
                emptyText={list.search ? 'По запросу ничего не найдено' : 'Выпусков пока нет'}
                filters={
                    <select
                        className={styles.filterSelect}
                        value={list.filters.journal_id === undefined ? '' : list.filters.journal_id}
                        onChange={(e) => list.setFilter('journal_id', e.target.value)}
                        aria-label="Фильтр по журналу"
                    >
                        <option value="">Все журналы</option>
                        {journals.map((j) => (
                            <option key={j.id} value={j.id}>
                                {j.name}
                            </option>
                        ))}
                    </select>
                }
            />
        </>
    );
};

export default ReleasesList;
